import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { Logo } from '../components/Logo';
import { CheckCircle2, XCircle, Loader2, RefreshCcw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// iter94.11 — Smarter polling for Stripe checkout confirmation.
//
// The old loop polled every 2s for 8 attempts (= 16s total) and then
// silently froze on "Processing…" with no retry. Stripe's webhook can
// legitimately take longer than 16s under load, so users were thinking
// payments had failed when they had actually succeeded.
//
// New plan:
//   • Polls 1s, 1s, 1s, 2s, 3s, 5s, 5s, 5s, 5s, 5s, 5s, 5s, 5s, 5s …
//     ≈ 7 attempts in the first 8s, totalling ~42s in 12 attempts.
//   • After 20s elapsed, swap copy to a calmer "Still processing —
//     this can take a few moments." and surface a clear Refresh
//     payment status button (replaces the silent reload).
//   • After the full window expires, show a calm support message with
//     the same Refresh button kept available — never a frozen state.
//
// All values centralised at top of the component so future tuning is
// trivial.
const POLL_DELAYS_MS = [1000, 1000, 1000, 2000, 3000, 5000, 5000, 5000, 5000, 5000, 5000, 5000];
const PATIENCE_THRESHOLD_MS = 20_000;  // when to swap copy

export default function BillingSuccess() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = sp.get('session_id');
  const memorialIdFromUrl = sp.get('memorial_id');
  const [state, setState] = useState({ status: 'checking', payment_status: 'pending' });
  const [err, setErr] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [windowDone, setWindowDone] = useState(false);
  const [pollGen, setPollGen] = useState(0);  // bumping this re-arms the loop
  const { refreshUser } = useAuth();
  // refreshUser is recreated on every AuthContext render. Stash it in a
  // ref so the polling effect doesn't get re-mounted (which previously
  // restarted the loop and rapidly burned through attempts).
  const refreshUserRef = useRef(refreshUser);
  useEffect(() => { refreshUserRef.current = refreshUser; }, [refreshUser]);

  // Lightweight elapsed-time tracker so the copy flips at the 20s mark
  // without coupling to the polling cadence.
  useEffect(() => {
    const startedAt = Date.now();
    setElapsedMs(0);
    const t = setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 500);
    return () => clearInterval(t);
  }, [pollGen]);

  // —— Polling effect ——
  // Owns its own cancelled flag + timer so re-mounts are safe.
  useEffect(() => {
    if (!sessionId) {
      setErr('No checkout session. Return to your billing page.');
      return;
    }
    setErr(null);
    setWindowDone(false);
    setState({ status: 'checking', payment_status: 'pending' });

    let cancelled = false;
    let timer = null;
    let attempt = 0;
    let lastError = null;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        setState(data);
        if (data.payment_status === 'paid') {
          try { await refreshUserRef.current?.(); } catch {}
          return;
        }
        if (data.status === 'expired') return;
        lastError = null;
      } catch (e) {
        lastError = e;
        console.warn('[billing-status] poll error:', e?.message || e);
      }

      if (cancelled) return;
      const i = attempt;
      attempt += 1;
      if (i < POLL_DELAYS_MS.length) {
        timer = setTimeout(poll, POLL_DELAYS_MS[i]);
      } else {
        // Window exhausted. If the *last* attempt was an error, surface
        // it gently; otherwise keep the calm "still waiting" state.
        if (lastError) setErr(formatApiError(lastError));
        setWindowDone(true);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, pollGen]);

  const handleManualRefresh = () => {
    // Bump pollGen to re-mount the polling effect from scratch.
    setPollGen((g) => g + 1);
  };

  const paid = state.payment_status === 'paid';
  const expired = state.status === 'expired';
  const beingPatient = !paid && !expired && !err && !windowDone && elapsedMs >= PATIENCE_THRESHOLD_MS;
  const exhausted = !paid && !expired && !err && windowDone;

  // iter95.3 — once payment is confirmed, automatically send the user back
  // to the memorial they just paid for. Brief delay so the success state
  // is visible for ~1.4s rather than blinking.
  useEffect(() => {
    if (!paid) return;
    const memId = memorialIdFromUrl || state.memorial_id;
    if (!memId) return;
    const t = setTimeout(() => {
      navigate(`/dashboard/m/${memId}?activated=1`, { replace: true });
    }, 1400);
    return () => clearTimeout(t);
  }, [paid, memorialIdFromUrl, state.memorial_id, navigate]);

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div
        className="ml-card max-w-lg w-full text-center plaque-shadow"
        data-testid="billing-success"
      >
        <Logo size={56} className="justify-center" />
        <div className="mt-6">
          {err ? (
            <>
              <XCircle className="w-10 h-10 mx-auto text-[hsl(var(--destructive))]" />
              <h1 className="font-serif text-3xl mt-3 text-[hsl(var(--primary))]">
                Something went wrong
              </h1>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">{err}</p>
              <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
                If your card was charged, our system will catch up automatically — head to your dashboard or click Refresh below.
              </p>
            </>
          ) : paid ? (
            <>
              <CheckCircle2 className="w-10 h-10 mx-auto text-[hsl(var(--accent))]" />
              <h1 className="font-serif text-3xl mt-3 text-[hsl(var(--primary))]">
                Thank you — your plan is active.
              </h1>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                You're now on the <strong className="capitalize">{state.plan_id}</strong> plan. Your memorial is unlocked with the features of this tier.
              </p>
            </>
          ) : expired ? (
            <>
              <XCircle className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]" />
              <h1 className="font-serif text-3xl mt-3 text-[hsl(var(--primary))]">
                Checkout expired
              </h1>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                Your session expired before payment was completed. Please try again.
              </p>
            </>
          ) : exhausted ? (
            // iter94.11 — Window expired without webhook landing. Calm
            // "we'll catch up automatically" copy + Refresh CTA stays.
            <>
              <Loader2 className="w-10 h-10 mx-auto text-[hsl(var(--primary)/0.55)]" />
              <h1
                className="font-serif text-2xl mt-3 text-[hsl(var(--primary))]"
                data-testid="billing-success-exhausted-title"
              >
                Still waiting on confirmation
              </h1>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                Stripe hasn't told us yet — but if your card was charged, your plan will activate automatically. You can safely close this tab. Your receipt arrives by email.
              </p>
              <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
                Need help? Email <a className="underline" href="mailto:support@myleaflegacy.com">support@myleaflegacy.com</a> with your name and we'll sort it.
              </p>
            </>
          ) : beingPatient ? (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-[hsl(var(--primary))] animate-spin" />
              <h1
                className="font-serif text-2xl mt-3 text-[hsl(var(--primary))]"
                data-testid="billing-success-patient-title"
              >
                Still processing your payment
              </h1>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                This can take a few moments. Your card has been authorised — we're just waiting on Stripe to confirm. You can leave this page open or refresh.
              </p>
            </>
          ) : (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-[hsl(var(--primary))] animate-spin" />
              <h1 className="font-serif text-3xl mt-3 text-[hsl(var(--primary))]">
                Confirming payment…
              </h1>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                {state.note || 'This usually takes a few seconds.'}
              </p>
            </>
          )}
        </div>

        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <Link
            to="/dashboard"
            className="ml-btn-primary"
            data-testid="success-dashboard-btn"
          >
            Back to dashboard
          </Link>
          {!paid && !expired && (
            <button
              type="button"
              onClick={handleManualRefresh}
              className="ml-btn-outline inline-flex items-center gap-2"
              data-testid="billing-success-refresh-btn"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
              Refresh payment status
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
